import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { refreshToken as fetchNewToken } from '../lib/apiClientStaff';

const StaffAuthContext = createContext(null);

export const StaffAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const refreshTimeout = useRef(null);

  // Set up automatic refresh based on exp in ms
  const setAuthTimeout = useCallback((expMs) => {
    const delay = expMs - Date.now() - 30_000;
    if (delay > 0) {
      refreshTimeout.current = setTimeout(async () => {
        try {
          const data = await fetchNewToken();
          const token = data.newAccessToken || data.token;
          const refreshToken = data.newRefreshToken || data.refreshToken;
          const decoded = jwtDecode(token);

          // Save all fields from the decoded token
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);
          localStorage.setItem('staffUsername', decoded.username || decoded.name || '');
          localStorage.setItem('staffRole', decoded.role || '');
          localStorage.setItem('staffUserId', decoded.staffId || '');
          localStorage.setItem('selectedBusinessId', decoded.businessId || '');

          setIsAuthenticated(true);
          setUser({
            username: decoded.username || decoded.name || '',
            role: decoded.role || '',
            staffId: decoded.staffId || '',
            businessId: decoded.businessId || '',
          });
          setAuthTimeout(decoded.exp * 1000);
        } catch (e) {
          // Remove all staff-related data on refresh failure
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('staffRole');
          localStorage.removeItem('staffUsername');
          localStorage.removeItem('staffUserId');
          localStorage.removeItem('selectedBusinessId');
          setIsAuthenticated(false);
          setUser(null);
        }
      }, delay);
    }
  }, []);

  // Always restore state from JWT, never just from localStorage
  const checkTokenExpiration = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      return;
    }
    try {
      const decoded = jwtDecode(token);
      const expired = decoded.exp * 1000 < Date.now();
      if (expired) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('staffRole');
        localStorage.removeItem('staffUsername');
        localStorage.removeItem('staffUserId');
        localStorage.removeItem('selectedBusinessId');
        setIsAuthenticated(false);
        setUser(null);
      } else {
        // Always update localStorage from JWT
        localStorage.setItem('staffUsername', decoded.username || decoded.name || '');
        localStorage.setItem('staffRole', decoded.role || '');
        localStorage.setItem('staffUserId', decoded.staffId || '');
        localStorage.setItem('selectedBusinessId', decoded.businessId || '');
        setIsAuthenticated(true);
        setUser({
          username: decoded.username || decoded.name || '',
          role: decoded.role || '',
          staffId: decoded.staffId || '',
          businessId: decoded.businessId || '',
        });
        setAuthTimeout(decoded.exp * 1000);
      }
    } catch (e) {
      // Force logout if token is corrupted
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('staffRole');
      localStorage.removeItem('staffUsername');
      localStorage.removeItem('staffUserId');
      localStorage.removeItem('selectedBusinessId');
      setIsAuthenticated(false);
      setUser(null);
    }
  }, [setAuthTimeout]);

  useEffect(() => {
    checkTokenExpiration();
    return () => {
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
    };
  }, [checkTokenExpiration]);

  // Accept tokens and profile info, always save all fields
  const login = (token, apiProfile = {}) => {
    const staffId = apiProfile.staffId ?? '';
    const username = apiProfile.name ?? '';
    const role = apiProfile.role ?? '';
    const businessId = apiProfile.businessId ?? '';
    localStorage.setItem('token', token);
    if (apiProfile.refreshToken) localStorage.setItem('refreshToken', apiProfile.refreshToken);
    localStorage.setItem('staffRole', role);
    localStorage.setItem('staffUsername', username);
    localStorage.setItem('staffUserId', staffId);
    localStorage.setItem('selectedBusinessId', businessId);
    setIsAuthenticated(true);
    setUser({ username, role, staffId, businessId });
    const decoded = jwtDecode(token);
    setAuthTimeout(decoded.exp * 1000);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('staffRole');
    localStorage.removeItem('staffUsername');
    localStorage.removeItem('staffUserId');
    localStorage.removeItem('selectedBusinessId');
    setIsAuthenticated(false);
    setUser(null);
    if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
  };

  return (
    <StaffAuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </StaffAuthContext.Provider>
  );
};

export const useAuthStaff = () => useContext(StaffAuthContext);